import {
  Button,
  DropdownField,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
} from '@one-thd/sui-atomic-components';
import { Search } from '@one-thd/sui-icons';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useForm, RegisterOptions } from 'react-hook-form';
import {
  IPHRequest,
  ISearchByMenuItems,
} from '../../../models/purchase-history';
import {
  IClearButton,
  ISearchBy,
} from '../../../models/purchase-history/index';
import {
  requestFormatter,
  searchByMenuItems,
} from '../../../utils/order-history/sort-and-sort-utils';
import { pushPurchaseHistorySearchEvent } from '../../../utils/purchase-history/analytics';

const ClearButton = ({
  handleClose,
  isMobile,
  pHRequest,
  setPHRequest,
  setSelectedSearchBy,
  setValue,
}: IClearButton) => {
  if (pHRequest.searchBy !== undefined) {
    if (isMobile) {
      return (
        <div className="sui-pt-6">
          <Button
            variant="secondary"
            onClick={() => {
              setValue('searchByTextfield', '');
              setSelectedSearchBy('itemDescription');
              setPHRequest((prevState: IPHRequest) => {
                return {
                  ...prevState,
                  searchBy: undefined,
                };
              });
              if (handleClose) handleClose();
            }}
            fullWidth
          >
            Clear Search
          </Button>
        </div>
      );
    }
    return (
      <button
        type="button"
        style={{ paddingTop: 25 }}
        className="sui-pl-1"
        onClick={() => {
          setValue('searchByTextfield', '');
          setSelectedSearchBy('itemDescription');
          setPHRequest((prevState: IPHRequest) => {
            return {
              ...prevState,
              searchBy: undefined,
            };
          });
        }}
      >
        Clear Search
      </button>
    );
  }
  return null;
};

export const SearchBy = ({
  customerType,
  handleClose,
  isMobile,
  pHRequest,
  setPHRequest,
}: ISearchBy) => {
  const searchByObj = pHRequest?.searchBy?.[0];
  const searchByKey: string | undefined =
    Object.entries(searchByObj || {})?.[0]?.[0] || undefined;
  const searchByValue: string =
    Object.entries(searchByObj || {})?.[0]?.[1] || '';
  const [selectedSearchBy, setSelectedSearchBy] = useState<string>(
    searchByKey || 'itemDescription',
  );

  const {
    formState: { errors },
    register,
    watch,
    setValue,
    clearErrors,
    unregister,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      searchByTextfield: searchByValue,
    },
    shouldUnregister: true,
  });

  const watchSearchByTextfield = watch('searchByTextfield');

  useEffect(() => {
    if (selectedSearchBy === 'paypal') {
      setPHRequest((prevState: IPHRequest) => {
        return {
          ...prevState,
          searchBy: [{ PayPal: '' }],
          pageNumber: 1,
        };
      });
      if (handleClose) handleClose();
    }
  }, [selectedSearchBy]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedSearchBy(event.target.value);
    setValue('searchByTextfield', '');
    clearErrors();
    unregister('searchByTextfield');
  };

  const getPlaceholderValue = (): string | undefined => {
    return searchByMenuItems(setValue).find(
      (item) => item.value === selectedSearchBy,
    )?.placeholder;
  };

  const getRegex = (): RegisterOptions<
    { searchByTextfield: string },
    'searchByTextfield'
  > => {
    const regexValue = searchByMenuItems(setValue).find(
      (item) => item.value === selectedSearchBy,
    )?.options;
    if (regexValue) {
      return regexValue as RegisterOptions<
        { searchByTextfield: string },
        'searchByTextfield'
      >;
    }
    return {};
  };

  const getErrorMessage = (type: string) => {
    return searchByMenuItems(setValue).find(
      (item) => item.value === selectedSearchBy,
    )?.errorMessages?.[type];
  };

  const getStatus = () => {
    if (errors.searchByTextfield) {
      return 'error';
    }
    return undefined;
  };

  const handleSumbit = () => {
    if (
      watchSearchByTextfield?.trim() === '' ||
      watchSearchByTextfield === undefined
    )
      return;
    const watchValue =
      selectedSearchBy === 'phoneNumber'
        ? watchSearchByTextfield.replaceAll('-', '')
        : watchSearchByTextfield;
    const value =
      selectedSearchBy && requestFormatter[selectedSearchBy](watchValue);

    // Analytics event call for search by id and value seleted by user.
    pushPurchaseHistorySearchEvent(watchSearchByTextfield, selectedSearchBy);

    if (
      selectedSearchBy === 'storeName' &&
      watchValue.toLowerCase() === 'online'
    ) {
      setPHRequest((prevState: IPHRequest) => {
        return {
          ...prevState,
          searchBy: [{ transSrc: 'WEB' }] || [],
          pageNumber: 1,
        };
      });
    } else {
      setPHRequest((prevState: IPHRequest) => {
        return {
          ...prevState,
          searchBy: value || [],
          pageNumber: 1,
        };
      });
    }
    if (handleClose) handleClose();
  };

  const ClearButtonProps: IClearButton = {
    isMobile,
    setValue,
    setSelectedSearchBy,
    pHRequest,
    setPHRequest,
    handleClose,
  };

  if (customerType === 'b2b') {
    return (
      <div className="sui-flex sui-flex-col sui-w-full md:sui-flex-row lg:sui-flex-row">
        <div className="sui-w-full md:sui-max-w-[202px] lg:sui-max-w-[202px]">
          <DropdownField
            id="search-by-dropdown"
            label="Search By"
            value={selectedSearchBy}
            onChange={handleChange}
            fullWidth
          >
            {searchByMenuItems(setValue).map(
              (item: ISearchByMenuItems, menuKey: number) => (
                <MenuItem key={menuKey} value={item.value}>
                  {item.name}
                </MenuItem>
              ),
            )}
          </DropdownField>
        </div>
        {selectedSearchBy !== 'paypal' && (
          <div className="sui-w-full md:sui-pt-5 md:sui-max-w-[332px] lg:sui-pt-5 lg:sui-max-w-[332px]">
            <TextField
              id="search-by-textfield"
              disabled={selectedSearchBy === undefined}
              placeholder={getPlaceholderValue()}
              status={getStatus()}
              statusMessage={
                errors.searchByTextfield
                  ? getErrorMessage(errors.searchByTextfield.type)
                  : undefined
              }
              onKeyPress={(event: { charCode: number }) => {
                if (event.charCode === 13) handleSumbit();
              }}
              InputProps={{
                ...register('searchByTextfield', getRegex()),
                endAdornment: !isMobile && (
                  <InputAdornment position="end">
                    <IconButton
                      disabled
                      aria-label="submit-search-by"
                      icon={Search}
                      onClick={() => handleSumbit()}
                    />
                  </InputAdornment>
                ),
                inputProps: { 'aria-label': 'search-by-textfield' },
              }}
              fullWidth
            />
          </div>
        )}
        <ClearButton {...ClearButtonProps} />
        {isMobile && (
          <div className="sui-pt-6">
            <Button
              disabled={
                watchSearchByTextfield === undefined ||
                watchSearchByTextfield?.trim() === '' ||
                errors.searchByTextfield !== undefined
              }
              onClick={() => handleSumbit()}
            >
              Accept
            </Button>
          </div>
        )}
      </div>
    );
  }

  return null;
};
